import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrgQuery } from '@/lib/hooks/useOrgQuery'
import { useNavigate } from '@tanstack/react-router'
import Editor, { type Monaco, loader } from '@monaco-editor/react'

// Pin the CDN-loaded Monaco to the same version whose types we have installed
// (see frontend/package.json devDependency monaco-editor). Otherwise the
// runtime API may differ from what TypeScript thinks is there.
loader.config({
  paths: {
    vs: 'https://cdn.jsdelivr.net/npm/monaco-editor@0.55.1/min/vs',
  },
})

// @monaco-editor/loader wraps its init promise in a "cancelable". When the
// hosting component unmounts before init resolves -- e.g. React StrictMode's
// double-invoke in dev, or HMR -- the wrapper calls cancel(), which rejects
// the underlying promise with the literal `{ type: "cancelation" }`. That
// object isn't an Error subclass, so nothing in @monaco-editor/react catches
// it and Bun's dev overlay surfaces it as an unhandled rejection. Silence
// only that specific shape; real loader failures still bubble.
if (typeof window !== 'undefined') {
  const FLAG = '__hawkMonacoCancelSilencer__'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any
  if (!w[FLAG]) {
    w[FLAG] = true
    window.addEventListener('unhandledrejection', (event) => {
      const reason = event.reason as { type?: unknown } | null
      if (
        reason &&
        typeof reason === 'object' &&
        reason.type === 'cancelation'
      ) {
        event.preventDefault()
      }
    })
  }
}
import {
  ArrowLeft,
  Check,
  CircleDashed,
  FileCode,
  Globe2,
  Library,
  Loader2,
  Lock,
  Play,
  Save,
  ShieldX,
  Trash2,
  TriangleAlert,
} from 'lucide-react'
import { toast } from 'sonner'
import { ApiError, apiFetch, apiJson, orgPath } from '@/lib/api/client'
import { useAuth } from '@/lib/auth/useAuth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { Policy } from '@/routes/_app/org/policies/index'

const GLOBAL_SCOPE_KEY = '__global__'

interface RepoListItem {
  name: string
  ecosystem: string
}

type Stage = 'metadata' | 'pre_fetch' | 'post_fetch' | 'serve' | 'rescan'

const STAGE_LABELS: Record<Stage, string> = {
  metadata: 'metadata (index lookup)',
  pre_fetch: 'pre_fetch (before download)',
  post_fetch: 'post_fetch (after download)',
  serve: 'serve (cache hit / miss)',
  rescan: 'rescan (background re-verify)',
}

/**
 * Worked policy examples shown in the "Start from" dropdown for new policies.
 * Each one is a self-contained, runnable source string -- the Rust runner
 * looks for the final `const __policy = definePolicy(...)` assignment.
 *
 * The set deliberately spans both `definePolicy` overloads (global and
 * stage-scoped) and covers different ctx fields, so authors see what's
 * possible without reading the preamble end-to-end.
 */
type Template = {
  key: string
  name: string
  description: string
  source: string
}

const TEMPLATES: Template[] = [
  {
    key: 'starter',
    name: 'Starter',
    description: 'Minimal scaffold runnable as-is.',
    source: `// Runs on every package request matching this policy's scope.
// Every path must end in 'return allow(...)' or 'return deny(...)';
// fall-through is a TypeScript error.
//
// 'Context', 'PackageInfo', 'allow', 'deny', and 'definePolicy' are all
// global -- hover any identifier or peek a type definition to explore.

const __policy = definePolicy(
  "Short human-readable description",
  (ctx: Context) => {
    if (ctx.package.yanked) {
      return deny("Yanked packages are blocked");
    }

    // Narrow on ctx.stage to access stage-specific fields:
    //
    //   if (ctx.stage === "post_fetch") {
    //     const sha = ctx.package.artifact.computed_sha256;
    //   }

    return allow("OK");
  },
);
`,
  },
  {
    key: 'block-yanked',
    name: 'Block yanked',
    description: 'pre_fetch policy that refuses any upstream-yanked package.',
    source: `// Stage-scoped to pre_fetch: the runtime only invokes this policy at
// the pre-download check, and ctx narrows to PreFetchContext so you can
// rely on ctx.package fields without ctx.stage narrowing.

const __policy = definePolicy(
  "Refuse upstream-yanked packages",
  "pre_fetch",
  (ctx) => {
    if (ctx.package.yanked) {
      return deny(\`\${ctx.package.name} (\${ctx.package.filename}) is yanked upstream\`);
    }
    return allow();
  },
);
`,
  },
  {
    key: 'size-limit',
    name: 'Artifact size limit',
    description: 'post_fetch cap on downloaded artifact bytes.',
    source: `// Stage-scoped to post_fetch -- ctx.package.artifact is reachable
// directly because Context narrows to PostFetchContext.

const MAX_BYTES = 100 * 1024 * 1024; // 100 MiB

const __policy = definePolicy(
  "Refuse artifacts larger than 100 MiB",
  "post_fetch",
  (ctx) => {
    const size = ctx.package.artifact.size_bytes;
    if (size > MAX_BYTES) {
      return deny(\`Artifact \${ctx.package.filename} is \${size} bytes (cap \${MAX_BYTES})\`);
    }
    return allow();
  },
);
`,
  },
  {
    key: 'author-allowlist',
    name: 'Author allowlist',
    description: 'pre_fetch admission gate keyed on package.author.',
    source: `// 'author' is sourced from upstream metadata; treat null as untrusted.

const TRUSTED_AUTHORS = new Set([
  "python",
  "pypa",
  "npm",
  "facebook",
  "google",
]);

const __policy = definePolicy(
  "Only admit packages from a trusted author allowlist",
  "pre_fetch",
  (ctx) => {
    const author = ctx.package.author;
    if (author === null || !TRUSTED_AUTHORS.has(author)) {
      return deny(\`Author "\${author ?? "unknown"}" is not on the allowlist\`);
    }
    return allow();
  },
);
`,
  },
  {
    key: 'name-blocklist',
    name: 'Name blocklist',
    description: 'metadata-stage deny list for known-bad package names.',
    source: `// Run at the metadata stage so we reject the package before it ever
// resolves to a downloadable artifact.

const BLOCKED = new Set<string>([
  "event-stream",
  "left-pad",
  "ua-parser-js",
]);

const __policy = definePolicy(
  "Deny known-malicious or quarantined package names",
  "metadata",
  (ctx) => {
    if (BLOCKED.has(ctx.package.name.toLowerCase())) {
      return deny(\`\${ctx.package.name} is on the security blocklist\`);
    }
    return allow();
  },
);
`,
  },
  {
    key: 'tamper-detection',
    name: 'Tamper detection',
    description: 'rescan policy that quarantines on hash divergence.',
    source: `// Background rescan re-hashes stored artifacts; if the result diverges
// from the upstream-declared sha256 we quarantine the file. Stage-scoped
// to rescan so ctx.package.artifact and ctx.package.sha256 are both typed.

const __policy = definePolicy(
  "Quarantine artifacts when computed hash diverges from declared hash",
  "rescan",
  (ctx) => {
    const computed = ctx.package.artifact.computed_sha256;
    const declared = ctx.package.sha256;
    if (declared && declared.toLowerCase() !== computed.toLowerCase()) {
      return deny(
        \`SHA-256 mismatch on \${ctx.package.filename}: declared \${declared.slice(0, 12)}..., computed \${computed.slice(0, 12)}...\`,
      );
    }
    return allow();
  },
);
`,
  },
  {
    key: 'ecosystem-conditional',
    name: 'Ecosystem conditional',
    description: 'all-stages policy that only enforces for one ecosystem.',
    source: `// Demonstrates the global definePolicy form combined with manual
// stage narrowing. The policy is registered for every stage but only
// enforces at pre_fetch when the package belongs to a specific ecosystem.

const __policy = definePolicy(
  "Require a Requires-Python marker on PyPI packages",
  (ctx: Context) => {
    if (ctx.package.ecosystem !== "pypi") {
      return allow("not a pypi package");
    }
    if (ctx.stage !== "pre_fetch") {
      return allow("only enforced on admission");
    }
    if (!ctx.package.requires_python) {
      return deny(\`PyPI package \${ctx.package.name} is missing Requires-Python\`);
    }
    return allow();
  },
);
`,
  },
]

const STARTER_SOURCE = TEMPLATES[0]!.source

interface PolicyEditorProps {
  /** `undefined` = creating a new policy. */
  policy?: Policy
}

export function PolicyEditor({ policy }: PolicyEditorProps) {
  const isNew = policy === undefined
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentOrgSlug } = useAuth()

  const [name, setName] = useState(policy?.name ?? '')
  const [description, setDescription] = useState(policy?.description ?? '')
  const [source, setSource] = useState(policy?.source ?? STARTER_SOURCE)
  const [scopeKey, setScopeKey] = useState(policy?.scope ?? GLOBAL_SCOPE_KEY)
  const [enabled, setEnabled] = useState(policy?.enabled ?? true)
  const [error, setError] = useState<string | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const repos = useOrgQuery<RepoListItem[]>(['admin-repositories'], '/admin/repositories', {
    staleTime: 60_000,
  })

  // The preamble is the canonical TypeScript that wraps every policy at
  // runtime; we render it inline (read-only, folded) above the user's code
  // so the editor reads like one typed file instead of asking authors to
  // mentally import unseen types.
  const preamble = useOrgQuery<{ source: string }>(
    ['admin-policies-preamble'],
    '/admin/policies/preamble',
    { staleTime: 60 * 60_000 },
  )

  const scope = scopeKey === GLOBAL_SCOPE_KEY ? null : scopeKey
  const dirty =
    isNew ||
    name !== policy!.name ||
    description !== policy!.description ||
    source !== policy!.source ||
    scope !== policy!.scope ||
    enabled !== policy!.enabled

  const save = useMutation({
    mutationFn: async () => {
      const body = JSON.stringify({
        name: name.trim(),
        description: description.trim(),
        source,
        scope,
        enabled,
      })
      return isNew
        ? apiJson<Policy>(orgPath('/admin/policies'), { method: 'POST', body })
        : apiJson<Policy>(orgPath(`/admin/policies/${policy!.id}`), {
            method: 'PATCH',
            body,
          })
    },
    onSuccess: (saved) => {
      toast.success(isNew ? `Created ${saved.name}` : `Saved ${saved.name}`)
      queryClient.invalidateQueries({ queryKey: ['admin-policies', currentOrgSlug] })
      queryClient.invalidateQueries({ queryKey: ['admin-policy', currentOrgSlug, saved.id] })
      if (isNew) {
        navigate({ to: '/org/policies/$id', params: { id: saved.id } })
      }
    },
    onError: (e: Error) => setError(e.message),
  })

  const del = useMutation({
    mutationFn: async () => {
      const r = await apiFetch(orgPath(`/admin/policies/${policy!.id}`), {
        method: 'DELETE',
      })
      if (!r.ok) throw new ApiError(r.status, await r.text())
    },
    onSuccess: () => {
      toast.success(`Deleted ${policy!.name}`)
      queryClient.invalidateQueries({ queryKey: ['admin-policies', currentOrgSlug] })
      navigate({ to: '/org/policies' })
    },
    onError: (e: Error) => setError(e.message),
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!name.trim()) return setError('Name is required')
    if (!source.trim()) return setError('Source is required')
    save.mutate()
  }

  return (
    <form className="px-10 py-8" onSubmit={submit}>
      <div className="mb-6 flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={() => navigate({ to: '/org/policies' })}
          className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-3.5" strokeWidth={1.5} />
          <span className="font-mono uppercase tracking-[0.22em]">
            All policies
          </span>
        </button>
        <div className="flex items-center gap-2">
          {!isNew && (
            <Button
              type="button"
              variant="ghost"
              className="text-muted-foreground hover:text-destructive"
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 className="size-4" />
              Delete
            </Button>
          )}
          <Button type="submit" disabled={save.isPending || (!dirty && !isNew)}>
            <Save className="size-4" />
            {save.isPending ? 'Saving...' : isNew ? 'Create policy' : 'Save changes'}
          </Button>
        </div>
      </div>

      <h1 className="font-display text-[44px] leading-[0.95] tracking-[-0.025em] text-foreground mb-2">
        {isNew ? 'New policy' : policy!.name}
      </h1>
      <p className="text-sm text-muted-foreground max-w-2xl mb-8">
        Policies run in an isolated sandbox with no filesystem, network, or
        host access.
      </p>

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[1fr_280px] mb-6">
        <Field label="Name">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="block-yanked-packages"
            maxLength={128}
            autoFocus={isNew}
          />
        </Field>
        <Field label="State">
          <button
            type="button"
            onClick={() => setEnabled((v) => !v)}
            className={`h-9 px-3 rounded-md border font-mono text-[11px] uppercase tracking-[0.18em] transition-colors inline-flex items-center justify-between gap-3 ${
              enabled
                ? 'border-primary/30 bg-primary/10 text-primary hover:bg-primary/15'
                : 'border-border bg-secondary text-muted-foreground hover:text-foreground'
            }`}
            aria-pressed={enabled}
          >
            <span>{enabled ? 'Enabled' : 'Disabled'}</span>
            <span
              className={`relative inline-block w-7 h-3.5 rounded-full transition-colors ${
                enabled ? 'bg-primary' : 'bg-border'
              }`}
            >
              <span
                className={`absolute top-0.5 size-2.5 rounded-full bg-background transition-all ${
                  enabled ? 'left-3.5' : 'left-0.5'
                }`}
              />
            </span>
          </button>
        </Field>
      </div>

      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[1fr_280px] mb-8">
        <Field label="Description">
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What does this policy enforce?"
          />
        </Field>
        <Field label="Scope">
          <Select value={scopeKey} onValueChange={setScopeKey}>
            <SelectTrigger aria-label="Scope">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={GLOBAL_SCOPE_KEY}>
                <span className="inline-flex items-center gap-2">
                  <Globe2 className="size-3.5" strokeWidth={1.5} />
                  Global (all repositories)
                </span>
              </SelectItem>
              {(repos.data ?? []).map((r) => (
                <SelectItem key={r.name} value={r.name}>
                  {r.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="font-mono text-[9px] uppercase tracking-[0.26em] text-muted-foreground font-medium">
              Source
            </span>
            <span className="h-px flex-1 bg-border" />
          </div>
          {isNew && (
            <TemplateGallery
              activeSource={source}
              onPick={(t) => {
                setSource(t.source)
                if (!description.trim()) setDescription(t.description)
              }}
            />
          )}
          {preamble.isLoading || !preamble.data ? (
            <div className="border border-border rounded-md bg-card/30 h-[540px] flex items-center justify-center">
              <Loader2
                className="size-5 text-primary animate-spin"
                strokeWidth={1.5}
              />
            </div>
          ) : (
            <TypedSourceEditor
              preamble={preamble.data.source}
              source={source}
              onChange={setSource}
            />
          )}
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            The final statement must be{' '}
            <span className="text-foreground/80">const __policy = definePolicy(...)</span>
          </p>
        </div>

        <DryRunPanel source={source} />
      </div>

      {error && (
        <div className="mt-6 border border-destructive/40 bg-destructive/10 rounded-md p-4 flex items-start gap-3">
          <TriangleAlert className="size-4 text-destructive shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-destructive mb-1">
              Save failed
            </div>
            <p className="text-sm text-destructive/90 break-all">{error}</p>
          </div>
        </div>
      )}

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {policy?.name}?</DialogTitle>
            <DialogDescription>
              The policy is removed from every replica within a second.
              Requests that depended on its allow/deny logic will pass through
              whichever policies remain.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={del.isPending}
              onClick={() => del.mutate()}
            >
              {del.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  )
}

// ── Dry-run panel ─────────────────────────────────────────────────────────

interface EvaluateResponse {
  effect: 'ALLOW' | 'DENY' | 'ERROR'
  reason: string
  elapsed_ms: number
}

function DryRunPanel({ source }: { source: string }) {
  const [stage, setStage] = useState<Stage>('pre_fetch')
  const [pkgName, setPkgName] = useState('example-package')
  const [ecosystem, setEcosystem] = useState('pypi')
  const [repository, setRepository] = useState('pypi-public')
  const [filename, setFilename] = useState('example_package-1.0.0-py3-none-any.whl')
  const [author, setAuthor] = useState('')
  const [yanked, setYanked] = useState(false)
  const [cacheHit, setCacheHit] = useState(false)
  const [result, setResult] = useState<EvaluateResponse | null>(null)

  const showArtifact = stage === 'post_fetch' || stage === 'rescan'
  const showCacheHit = stage === 'serve'

  /**
   * Build the exact JSON the backend deserializes into `PolicyContext`.
   * Mirrors `context.rs` -- `#[serde(tag = "stage", rename_all = "snake_case")]`.
   */
  function buildContext(): unknown {
    const pkg = {
      name: pkgName.trim(),
      ecosystem,
      repository,
      filename,
      sha256: null,
      size_bytes: 12345,
      upstream_url: null,
      yanked,
      requires_python: null,
      author: author.trim() || null,
    }
    const artifact = {
      computed_sha256:
        'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
      size_bytes: 12345,
      storage_path: `storage/${repository}/${filename}`,
    }
    switch (stage) {
      case 'metadata':
      case 'pre_fetch':
        return { stage, package: pkg }
      case 'post_fetch':
      case 'rescan':
        return { stage, package: { ...pkg, artifact } }
      case 'serve':
        return { stage, package: pkg, cache_hit: cacheHit }
    }
  }

  const evaluate = useMutation({
    mutationFn: () =>
      apiJson<EvaluateResponse>(orgPath('/admin/policies/evaluate'), {
        method: 'POST',
        body: JSON.stringify({ source, context: buildContext() }),
      }),
    onSuccess: setResult,
    onError: (e: Error) => {
      setResult({ effect: 'ERROR', reason: e.message, elapsed_ms: 0 })
    },
  })

  // Reset the previous verdict if the user edits the source -- stale results
  // are worse than no result here.
  useEffect(() => {
    setResult(null)
  }, [source])

  return (
    <aside className="lg:sticky lg:top-6 self-start">
      <div className="flex items-center gap-2 mb-3">
        <span className="font-mono text-[9px] uppercase tracking-[0.26em] text-muted-foreground font-medium">
          Dry-run
        </span>
        <span className="h-px flex-1 bg-border" />
      </div>
      <div className="border border-border bg-card rounded-md">
        <div className="p-4 border-b border-border space-y-3">
          <Field label="Stage" compact>
            <Select value={stage} onValueChange={(v) => setStage(v as Stage)}>
              <SelectTrigger aria-label="Stage">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(STAGE_LABELS) as Stage[]).map((s) => (
                  <SelectItem key={s} value={s}>
                    {STAGE_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <div className="pt-2 flex items-baseline gap-2 border-t border-border/60">
            <span className="font-mono text-[10px] text-primary/90">ctx</span>
            <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
              fields
            </span>
          </div>

          <Field label="package.name" compact>
            <Input value={pkgName} onChange={(e) => setPkgName(e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="package.ecosystem" compact>
              <Select value={ecosystem} onValueChange={setEcosystem}>
                <SelectTrigger aria-label="package.ecosystem">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pypi">pypi</SelectItem>
                  <SelectItem value="npm">npm</SelectItem>
                  <SelectItem value="cargo">cargo</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="package.repository" compact>
              <Input
                value={repository}
                onChange={(e) => setRepository(e.target.value)}
              />
            </Field>
          </div>
          <Field label="package.filename" compact>
            <Input value={filename} onChange={(e) => setFilename(e.target.value)} />
          </Field>
          <Field label="package.author" compact>
            <Input
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="(null)"
            />
          </Field>
          <div className="flex flex-wrap gap-x-4 gap-y-2 pt-1">
            <Toggle label="package.yanked" value={yanked} onChange={setYanked} />
            {showCacheHit && (
              <Toggle label="cache_hit" value={cacheHit} onChange={setCacheHit} />
            )}
          </div>
          {showArtifact && (
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground pt-1">
              package.artifact is auto-populated for {stage}
            </p>
          )}
        </div>

        <div className="p-4 border-b border-border">
          <Button
            type="button"
            className="w-full"
            disabled={evaluate.isPending}
            onClick={() => evaluate.mutate()}
          >
            {evaluate.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Play className="size-4" />
            )}
            {evaluate.isPending ? 'Evaluating...' : 'Evaluate'}
          </Button>
        </div>

        <ResultPane result={result} />
      </div>
    </aside>
  )
}

// ── Typed source editor ───────────────────────────────────────────────────
//
// Monaco is the same engine VS Code uses, including the real TypeScript
// language service running in a Web Worker. We register the runtime preamble
// as an ambient lib so the user's policy.ts sees every global it would have
// at evaluation time -- `definePolicy`, `Context`, `allow`, `deny` -- which
// gives hover types, autocompletion on `ctx.package.`, and red squiggles for
// genuine type errors. No prepending of the preamble into the editor doc.

function TypedSourceEditor({
  preamble,
  source,
  onChange,
}: {
  preamble: string
  source: string
  onChange: (s: string) => void
}) {
  // Configure via Editor's own beforeMount instead of useMonaco. The Editor
  // component catches the loader's cancelation rejection internally; the
  // useMonaco hook does not, which is why StrictMode double-mount surfaced
  // an unhandled rejection. beforeMount runs once per mount with monaco
  // already resolved -- no cleanup-cancel race.
  function beforeMount(monaco: Monaco) {
    const ts = monaco.typescript
    ts.typescriptDefaults.setCompilerOptions({
      // ES2020 is the newest target Monaco's bundled tsserver exposes; the
      // policy runtime is Deno-core (V8) so this is far below its capability.
      target: ts.ScriptTarget.ES2020,
      module: ts.ModuleKind.ESNext,
      moduleResolution: ts.ModuleResolutionKind.NodeJs,
      strict: true,
      // Monaco's TS defaults don't always expand `strict` to every sub-flag,
      // so we set the load-bearing ones explicitly. `noImplicitReturns` is
      // what flags `validate` bodies that fall through without `return
      // allow(...)` / `return deny(...)` -- the whole point of typing
      // validate as `() => never`.
      strictNullChecks: true,
      noImplicitReturns: true,
      allowNonTsExtensions: true,
      noLib: false,
      lib: ['es2020'],
    })
    ts.typescriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: false,
      noSyntaxValidation: false,
    })
    // Inlay hints render inferred parameter and return types as gray inline
    // annotations -- so `(ctx) => ...` visibly displays as `(ctx: Context) =>`
    // without forcing the author to type the annotation themselves. The
    // matching `inlayHints.enabled` flag in the editor options below tells
    // Monaco to actually render the hints.
    ts.typescriptDefaults.setInlayHintsOptions({
      includeInlayParameterNameHints: 'all',
      includeInlayParameterNameHintsWhenArgumentMatchesName: false,
      includeInlayFunctionParameterTypeHints: true,
      includeInlayVariableTypeHints: true,
      includeInlayPropertyDeclarationTypeHints: true,
      includeInlayFunctionLikeReturnTypeHints: true,
      includeInlayEnumMemberValueHints: true,
    })
    // The preamble file has no imports/exports, so its top-level
    // declarations become global -- exactly mirroring what the runtime does
    // when it concatenates preamble + policy at evaluation time. Calling
    // addExtraLib with the same path on a re-mount just replaces the lib,
    // so no leak; we deliberately do not dispose on unmount because the
    // singleton TS service outlives any individual editor.
    const preambleUri = monaco.Uri.parse('file:///hawk-policy-preamble.ts')
    ts.typescriptDefaults.addExtraLib(preamble, preambleUri.toString())
    // Also create an editor model for the preamble. addExtraLib feeds the
    // TS language service so hover and completion work, but "Peek Type
    // Definition" / "Go to Type Definition" needs to *open* the source file
    // in the peek widget, which only works if a model exists for that URI.
    // The model is global to monaco.editor; create-if-absent keeps remounts
    // and HMR from leaking duplicates.
    if (!monaco.editor.getModel(preambleUri)) {
      monaco.editor.createModel(preamble, 'typescript', preambleUri)
    }
  }

  // Tabbed model switching: same Monaco instance, different paths. Monaco
  // preserves cursor/scroll position per model, so flipping between the
  // policy and the preamble feels exactly like switching files in VS Code.
  // The preamble tab is read-only -- its model was created in beforeMount.
  const [activeFile, setActiveFile] = useState<'policy' | 'preamble'>('policy')
  const isPolicy = activeFile === 'policy'

  return (
    <div className="border border-border rounded-md overflow-hidden bg-[#1e1e1e]">
      <FileTabs active={activeFile} onChange={setActiveFile} />
      <Editor
        height="510px"
        language="typescript"
        path={
          isPolicy
            ? 'file:///policy.ts'
            : 'file:///hawk-policy-preamble.ts'
        }
        value={isPolicy ? source : preamble}
        onChange={(v) => {
          if (isPolicy) onChange(v ?? '')
        }}
        theme="vs-dark"
        beforeMount={beforeMount}
        options={{
          minimap: { enabled: false },
          fontFamily: '"JetBrains Mono", ui-monospace, monospace',
          fontSize: 13,
          tabSize: 2,
          wordWrap: 'on',
          scrollBeyondLastLine: false,
          renderLineHighlight: 'gutter',
          smoothScrolling: true,
          padding: { top: 12, bottom: 12 },
          lineNumbersMinChars: 3,
          fixedOverflowWidgets: true,
          inlayHints: { enabled: 'on' },
          readOnly: !isPolicy,
          // Subtle visual cue when looking at the read-only preamble.
          renderValidationDecorations: isPolicy ? 'on' : 'off',
        }}
        loading={
          <div className="h-[510px] flex items-center justify-center">
            <Loader2
              className="size-5 text-primary animate-spin"
              strokeWidth={1.5}
            />
          </div>
        }
      />
    </div>
  )
}

/**
 * File-tab strip that mirrors a VS Code editor group: each tab swaps the
 * underlying Monaco model via the Editor's `path` prop. Active state uses
 * the same dark background as the editor body so the active tab visually
 * merges into the workspace below it.
 */
function FileTabs({
  active,
  onChange,
}: {
  active: 'policy' | 'preamble'
  onChange: (f: 'policy' | 'preamble') => void
}) {
  return (
    <div className="flex bg-[#1a1a1a] border-b border-border/80">
      <FileTab active={active === 'policy'} onClick={() => onChange('policy')}>
        <FileCode className="size-3 text-primary" strokeWidth={1.5} />
        policy.ts
      </FileTab>
      <FileTab
        active={active === 'preamble'}
        onClick={() => onChange('preamble')}
      >
        <Lock className="size-3 text-muted-foreground" strokeWidth={1.5} />
        preamble.ts
        <span className="ml-1 font-mono text-[8px] uppercase tracking-[0.18em] text-muted-foreground/80">
          read-only
        </span>
      </FileTab>
    </div>
  )
}

function FileTab({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative px-4 h-9 inline-flex items-center gap-1.5 font-mono text-[11px] transition-colors border-r border-border/60 ${
        active
          ? 'bg-[#1e1e1e] text-foreground'
          : 'bg-[#1a1a1a] text-muted-foreground hover:text-foreground hover:bg-[#1c1c1c]'
      }`}
    >
      {active && (
        <span
          className="absolute top-0 left-0 right-0 h-[2px] bg-primary"
          aria-hidden
        />
      )}
      {children}
    </button>
  )
}

function ResultPane({ result }: { result: EvaluateResponse | null }) {
  if (!result) {
    return (
      <div className="p-5 text-center text-muted-foreground font-mono text-[10px] uppercase tracking-[0.22em]">
        Run to see verdict
      </div>
    )
  }

  const accent =
    result.effect === 'ALLOW'
      ? 'text-primary border-l-primary'
      : result.effect === 'DENY'
        ? 'text-destructive border-l-destructive'
        : 'text-amber-500 border-l-amber-500'

  const Icon =
    result.effect === 'ALLOW'
      ? Check
      : result.effect === 'DENY'
        ? ShieldX
        : CircleDashed

  return (
    <div className={`border-l-2 p-4 ${accent}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="size-3.5" strokeWidth={1.5} />
        <span className="font-mono text-[10px] uppercase tracking-[0.22em]">
          {result.effect}
        </span>
        <span className="ml-auto font-mono text-[10px] text-muted-foreground tabular-nums">
          {result.elapsed_ms} ms
        </span>
      </div>
      <p className="text-xs text-foreground/85 break-words leading-relaxed">
        {result.reason || '(no reason provided)'}
      </p>
    </div>
  )
}

// ── helpers ───────────────────────────────────────────────────────────────

function Field({
  label,
  children,
  compact,
}: {
  label: string
  children: React.ReactNode
  compact?: boolean
}) {
  // Wrapping the control inside `<label>` gives plain inputs implicit name
  // association (axe `label` rule passes) without manual id wiring. Buttons
  // and Radix `SelectTrigger` children must still carry their own
  // `aria-label` since `<label>` only auto-associates with form controls.
  return (
    <label className={compact ? 'grid gap-1' : 'grid gap-2'}>
      <span
        className={`font-mono uppercase tracking-[0.22em] text-muted-foreground ${
          compact ? 'text-[9px]' : 'text-[10px]'
        }`}
      >
        {label}
      </span>
      {children}
    </label>
  )
}

/**
 * Visible gallery of worked examples shown above the editor for the
 * new-policy flow. Rendering inline (vs. a dropdown trigger) means the
 * "blank canvas" moment shows authors what's possible without a click --
 * picking a template loads it into the editor and stays highlighted so
 * authors can switch freely while still scaffolding.
 *
 * Hidden once a policy is saved; clobbering an existing policy via
 * templates would be destructive without a warning, and the saved source
 * is already the chosen starting point.
 */
function TemplateGallery({
  activeSource,
  onPick,
}: {
  activeSource: string
  onPick: (t: Template) => void
}) {
  // `active` is null when the source no longer matches any template byte-for-byte,
  // i.e. the user has typed edits we'd be about to throw away.
  const active = TEMPLATES.find((t) => t.source === activeSource) ?? null
  const [pending, setPending] = useState<Template | null>(null)

  function requestPick(t: Template) {
    if (active?.key === t.key) return
    if (active === null) {
      setPending(t)
      return
    }
    onPick(t)
  }

  return (
    <div className="mb-4 border border-border rounded-md bg-card/40 overflow-hidden">
      <div className="px-4 py-3 border-b border-border/60 flex items-center gap-2">
        <Library className="size-3.5 text-primary" strokeWidth={1.5} />
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-foreground/80 font-medium">
          Start from a template
        </span>
        <span className="ml-auto font-mono text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
          {TEMPLATES.length} examples
        </span>
      </div>
      <div className="p-3 flex flex-wrap gap-2">
        {TEMPLATES.map((t) => {
          const isActive = active?.key === t.key
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => requestPick(t)}
              className={`px-3 h-8 rounded-md text-[12px] font-medium transition-all ${
                isActive
                  ? 'bg-primary text-primary-foreground ring-1 ring-primary/50 shadow-[0_0_0_3px_rgba(0,0,0,0)]'
                  : 'bg-secondary text-foreground/80 border border-transparent hover:border-primary/40 hover:text-foreground'
              }`}
              title={t.description}
            >
              {t.name}
            </button>
          )
        })}
      </div>
      <div className="px-4 py-2 border-t border-border/60 bg-background/40 min-h-[36px] flex items-center">
        {active ? (
          <p className="text-[11px] text-muted-foreground leading-snug">
            <span className="font-mono text-foreground/80">{active.name}</span>
            {' -- '}
            {active.description}
          </p>
        ) : (
          <p className="text-[11px] text-muted-foreground italic leading-snug">
            Custom source -- pick a template above to reset.
          </p>
        )}
      </div>
      <Dialog
        open={pending !== null}
        onOpenChange={(o) => {
          if (!o) setPending(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Replace your edits?</DialogTitle>
            <DialogDescription>
              You have unsaved changes in <span className="font-mono">policy.ts</span>.
              Loading the <span className="font-mono">{pending?.name}</span> template
              will overwrite them. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPending(null)}
            >
              Keep my edits
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (pending) onPick(pending)
                setPending(null)
              }}
            >
              Replace with template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function Toggle({
  label,
  value,
  onChange,
}: {
  label: string
  value: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
        value ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
      }`}
      aria-pressed={value}
    >
      <span
        className={`relative inline-block w-6 h-3 rounded-full transition-colors ${
          value ? 'bg-primary' : 'bg-border'
        }`}
      >
        <span
          className={`absolute top-0.5 size-2 rounded-full bg-background transition-all ${
            value ? 'left-3.5' : 'left-0.5'
          }`}
        />
      </span>
      {label}
    </button>
  )
}

export type { Policy }
